import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import type { FeaturedProdBlock as Value } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";

type Props = Immutable<{
    value: Value;
}>;

/**
 * React port of the pillows-specific ``FeaturedProdBlock`` (defined in
 * ``server/cms/components/featured_prod.py``). DOM mirrors the legacy
 * Django template ``cms/components/featured_prod.html`` exactly: header
 * (headline + description), product info (description, price block,
 * shop-button link), featured image, value-props list, and special-offer
 * footer.
 *
 * The price block uses ``purchase_info`` and ``show_starting_at`` fields
 * augmented server-side by ``get_data_for_client_render`` (mirroring
 * ``PopularProductsBlock.ProductBlock``). The block also defines an unused
 * ``show_strikethrough`` field on the model — preserved in the schema for
 * legacy compatibility but inert in the legacy template, so this component
 * does NOT read it.
 *
 * The "Starting at " prefix is hardcoded English in the legacy template;
 * here it is wrapped in ttag for i18n.
 */
const renderPrice = (
    info: Immutable<NonNullable<Value["purchase_info"]>>,
    showStartingAt: boolean,
): React.JSX.Element | null => {
    if (!info.price.exists) {
        return null;
    }
    const cosmetic = info.price.cosmetic_excl_tax;
    if (!cosmetic) {
        return null;
    }
    const retail = info.price.retail;
    const showRetail = retail !== null && retail !== cosmetic;
    if (!showRetail) {
        return (
            <p className="section-info-sleep-home__info__price--actual">
                {showStartingAt && <span>{t`Starting at `}</span>}
                {cosmetic}
            </p>
        );
    }
    return (
        <>
            <p className="section-info-sleep-home__info__price--retail">
                {showStartingAt && <span>{t`Starting at `}</span>}
                <span className="section-info-sleep-home__info__price--strike">
                    {retail}
                </span>
            </p>
            <p className="section-info-sleep-home__info__price--highlight">
                {cosmetic}
            </p>
        </>
    );
};

export const FeaturedProdBlock = (props: Props): React.JSX.Element => {
    const v = props.value;
    const valueProps = v.value_props ?? [];
    const purchaseInfo = v.purchase_info;
    const showStartingAt = !!v.show_starting_at;
    const shopButtonUrl = v.shop_button?.url ?? "";
    const imageUrl = v.image?.url ?? "";
    return (
        <div className="l-full-width section-info-sleep">
            <div className="l-capped-width section-info-sleep__inner">
                <div className="section-info-sleep__header">
                    <div className="section-info-sleep__title">
                        <RichTextBlock value={v.headline ?? ""} />
                    </div>
                    <div className="section-info-sleep__copy">
                        <RichTextBlock value={v.description ?? ""} />
                    </div>
                </div>
                <div className="section-info-sleep-home">
                    <div className="section-info-sleep-home__info">
                        <div className="section-info-sleep-home__info__desc">
                            <RichTextBlock
                                value={v.product_description ?? ""}
                            />
                        </div>
                        <div className="section-info-sleep-home__info__price">
                            {purchaseInfo &&
                                renderPrice(purchaseInfo, showStartingAt)}
                        </div>
                        <a
                            className="section-info-sleep-home__link"
                            href={shopButtonUrl}
                        >
                            <div className="button button-lochmara-pillows">
                                {v.shop_button_name ?? ""}
                            </div>
                        </a>
                    </div>
                    <img
                        className="section-info-sleep-home__image responsive-img"
                        src={imageUrl}
                        alt=""
                        loading="lazy"
                        decoding="async"
                    />
                    <div className="section-info-sleep-home__value-props-container">
                        {valueProps.map((prop, i) => {
                            const propImageUrl = prop.image?.url ?? "";
                            return (
                                <div
                                    key={i}
                                    className="section-info-sleep-home__value-props"
                                >
                                    <div className="section-info-sleep__value-props__icon">
                                        <div
                                            className="section-info-sleep-home__value-props__icon-item"
                                            style={{
                                                backgroundImage: `url(${propImageUrl})`,
                                            }}
                                        ></div>
                                    </div>
                                    <div className="section-info-sleep__value-props__copy">
                                        <div className="section-info-sleep__value-props__title">
                                            {prop.title}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className="section-info-sleep-home__refer-friend">
                    <RichTextBlock value={v.special_offer_text ?? ""} />
                </div>
            </div>
        </div>
    );
};
